/**
 * Sender perspective label for signatures.
 *
 * Given the recipient relationship (e.g. "grandparent"), returns the sender role
 * (e.g. "grandchild") so the signature reads correctly: "From your grandchild".
 */

const SENDER_PERSPECTIVE: Record<string, string> = {
  parent: 'child',
  grandparent: 'grandchild',
  sibling: 'sibling',
  child: 'parent',
  spouse: 'partner',
  partner: 'partner',
  friend: 'friend',
  mentor: 'student',
  teacher: 'student',
  colleague: 'colleague',
  crush: 'secret admirer',
  other: 'loved one',
};

function normalizeRelationship(input: string) {
  return input.toLowerCase().trim().replace(/[^a-z]/g, '');
}

/**
 * Robustly resolves the sender label.
 *
 * Important: avoids substring collisions like "grandparent" matching "parent".
 */
export function getSenderPerspectiveLabel(relationship?: string | null): string {
  if (!relationship) return 'loved one';

  const normalized = normalizeRelationship(relationship);
  if (!normalized) return 'loved one';

  // Exact match first (covers enum values like "grandparent")
  if (SENDER_PERSPECTIVE[normalized]) return SENDER_PERSPECTIVE[normalized];

  // Then longest-key-first substring match (covers labels like "Spouse/Partner")
  const keys = Object.keys(SENDER_PERSPECTIVE).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (normalized.includes(key)) return SENDER_PERSPECTIVE[key];
  }

  return 'loved one';
}
